import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  LinearProgress,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { Share as ShareIcon } from "@material-ui/icons";
import MUIDataTable from "mui-datatables";

// components
import PageTitle from "../../components/PageTitle";

// styles
import useStyles from "./styles";

// constants
import { SERVER } from "../../constants";

// context
import { useGroupState } from "../../context/GroupContext";

// localization
import { useTranslation, useLeagueNames, useLanguage } from "../../localization";

export default function Leaderboards(props) {
  var classes = useStyles();
  var t = useTranslation();
  var leagueOverrides = useLeagueNames();
  var { language } = useLanguage();
  // The selected group determines which standings to load; `view` ("aggregate"
  // or a league slug) is validated by the route in Layout before we render.
  var { selectedGroup, leagues, leagueNames } = useGroupState();
  var view = props.view;

  const [allInfo, setAllInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Pull the whole group's standings in one /allinfo call; tabs switch between
  // views client-side without refetching. Remounts on group change via the
  // route `key`, so this also re-runs when the user switches groups.
  useEffect(() => {
    setIsLoading(true);
    var url = new URL(SERVER + "/allinfo");
    url.search = new URLSearchParams({ group: selectedGroup }).toString();
    fetch(url)
      .then(response => response.json())
      .then(
        json => {
          setAllInfo(json);
          setIsLoading(false);
        },
        // Note: it's important to handle errors here instead of a catch() block
        // so that we don't swallow exceptions from actual bugs in components.
        error => {
          console.debug(error);
          alert(t.leaderboards.couldNotLoad);
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  function handleTabChange(event, value) {
    props.history.push("/app/leaderboards/" + value);
  }

  // The player list backing the active view.
  var players = [];
  if (allInfo) {
    if (view === "aggregate") {
      players = allInfo.players || [];
    } else {
      var league = (allInfo.leagues || []).find(l => l.name === view);
      players = league ? league.players || [] : [];
    }
  }

  // Sort + rank exactly like the bot's `standings` command: by total points
  // (settled + live) descending, with ties sharing a place number.
  var sorted = players
    .slice()
    .sort(
      (a, b) =>
        b.points + (b.live_points || 0) - (a.points + (a.live_points || 0)),
    );
  // Only split out settled/live columns when there are live points to show.
  var showLive = sorted.some(p => "live_points" in p);

  var lastTotal = null;
  var place = 0;
  var rows = sorted.map(player => {
    var total = player.points + (player.live_points || 0);
    if (lastTotal === null || total !== lastTotal) {
      place += 1;
    }
    lastTotal = total;
    return showLive
      ? [place, player.name, player.points, player.live_points || 0, total]
      : [place, player.name, total];
  });

  function column(label) {
    return {
      name: label,
      options: {
        customHeadLabelRender: () => (
          <Typography variant="h5">{label}</Typography>
        ),
        filter: false,
        sort: false,
      },
    };
  }
  var columns = (showLive
    ? [
        t.leaderboards.rank,
        t.leaderboards.player,
        t.leaderboards.settled,
        t.leaderboards.live,
        t.leaderboards.total,
      ]
    : [t.leaderboards.rank, t.leaderboards.player, t.leaderboards.points]
  ).map(column);

  // Render the active leaderboard as plain text matching the bot's `standings`
  // command, so a shared copy looks the same in chat.
  function buildShareText() {
    var header =
      view === "aggregate"
        ? t.leaderboards.aggregateHeader
        : leagueOverrides[view] ||
          ((allInfo.leagues || []).find(l => l.name === view) || {})
            .official_name ||
          leagueNames[view] ||
          view;
    var lines = [header];
    var last = null;
    var p = 0;
    sorted.forEach(player => {
      var total = player.points + (player.live_points || 0);
      if (last === null || total !== last) {
        p += 1;
      }
      last = total;
      var breakdown =
        "live_points" in player
          ? " (" + player.points + " + " + player.live_points + ")"
          : "";
      lines.push("    " + p + ". " + player.name + ": " + total + breakdown);
    });
    return lines.join("\n");
  }

  function copyToClipboard() {
    var text = buildShareText();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => setCopied(true),
        error => console.debug(error),
      );
      return;
    }
    // Fallback for non-secure contexts (e.g. served over http on the LAN),
    // where navigator.clipboard is unavailable.
    var textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
    } catch (error) {
      console.debug(error);
    }
    document.body.removeChild(textarea);
  }

  return (
    <>
      <PageTitle
        title={t.leaderboards.title}
        button={
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ShareIcon />}
            disabled={isLoading || rows.length === 0}
            onClick={copyToClipboard}
          >
            {t.common.share}
          </Button>
        }
      />
      <Tabs
        value={view}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        className={classes.tabs}
      >
        <Tab label={t.leaderboards.aggregate} value="aggregate" />
        {leagues.map(slug => (
          <Tab
            key={slug}
            label={leagueOverrides[slug] || leagueNames[slug] || slug}
            value={slug}
          />
        ))}
      </Tabs>
      <Grid container spacing={4}>
        <Grid item xs={12} className={classes.table}>
          <MUIDataTable
            key={language}
            title=""
            data={rows}
            columns={columns}
            options={{
              filterType: "checkbox",
              selectableRows: "none",
              download: false,
              print: false,
              search: false,
              filter: false,
              viewColumns: false,
              pagination: false,
              textLabels: {
                body: {
                  noMatch: isLoading ? <LinearProgress /> : t.leaderboards.noStandings,
                },
              },
            }}
          />
        </Grid>
      </Grid>
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        message={t.leaderboards.copied}
        onClose={() => setCopied(false)}
      />
    </>
  );
}
